import moment from 'moment-timezone';
import { useToast } from '@medely/ui-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useProNavigate } from '@mp/contexts';
import { setDateToTimeMoment } from '@mp/components/clockOutFlow/ClockOutForm/datetime';
import { EditClockTimesFormValues } from './schema';

export type EditClockTimesPayload = {
  jobId: number;
  formValues: EditClockTimesFormValues;
  timezone: string;
};

function buildPayload({ formValues, timezone }: EditClockTimesPayload): {
  approved_clock_in: string;
  approved_clock_out: string;
  pro_edit_reason: string;
  notes?: string;
} {
  const clockInDateInTz = moment.tz(formValues.approved_clock_in_date, 'YYYY-MM-DD', timezone);
  const clockOutDateInTz = moment.tz(formValues.approved_clock_out_date, 'YYYY-MM-DD', timezone);
  const clockInMoment = setDateToTimeMoment(
    clockInDateInTz,
    formValues.approved_clock_in_time,
    false,
  );
  const clockOutMoment = setDateToTimeMoment(
    clockOutDateInTz,
    formValues.approved_clock_out_time,
    false,
  );

  if (!clockInMoment || !clockOutMoment) {
    throw new Error('Invalid clock in or clock out time');
  }

  if (!formValues.reason) {
    throw new Error('Reason is required');
  }

  return {
    approved_clock_in: clockInMoment.tz(timezone).format(),
    approved_clock_out: clockOutMoment.tz(timezone).format(),
    pro_edit_reason: formValues.reason,
    ...(formValues.reason === 'other' && formValues.reason_other
      ? { notes: formValues.reason_other }
      : {}),
  };
}

export const useEditClockTimesMutation = (jobId: number) => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { navigate } = useProNavigate();

  const {
    mutate: editClockTimes,
    isPending,
    error,
  } = useMutation(
    (payload: EditClockTimesPayload) => {
      const body = buildPayload(payload);
      return axios.post(`/v3/professional/jobs/${jobId}/clock_time_edit_request`, body);
    },
    {
      onSuccess: (_response, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['job', variables.jobId],
        });
        queryClient.invalidateQueries({
          queryKey: ['jobs'],
        });

        toast.success(_response.data.message),
          {
            name: 'check',
          };
        navigate('JobDetails', { id: variables.jobId });
      },
      onError: (e: {
        response?: { data?: { message?: string }; message?: string };
        message?: string;
      }) => {
        console.error('Error submitting clock time edit request', { error: e, jobId });
      },
    },
  );

  const errorMessage =
    error !== null
      ? (error as { response?: { data?: { message?: string } }; message?: string }).response?.data
          ?.message ??
        (error as { message?: string }).message ??
        'Something went wrong.'
      : null;

  return {
    editClockTimes,
    isLoading: isPending,
    errorMessage,
  };
};
